(function () {
  'use strict';

  const selectorName = '.pods-fragrance-selector';
  const formSelector = 'form[action*="/cart/add"], form.wt-product__add-to-cart_form, form[data-type="add-to-cart-form"], form[id^="product-form-"]';
  // Mirrored purchase controls: the theme's sticky bar plus any pods CTA elsewhere on the page.
  const ctaSelector = '.wt-product__sticky-buy__button, [data-pods-cta]';

  function escapeHtml(value) {
    const element = document.createElement('div');
    element.textContent = String(value || '');
    return element.innerHTML;
  }

  function safeUrl(value) {
    const url = String(value || '').trim();
    if (!url || !/^(https?:)?\/\//i.test(url)) return '';
    try {
      return escapeHtml(encodeURI(url));
    } catch (error) {
      return '';
    }
  }

  // For URLs that land inside url('...') in a style attribute. encodeURI leaves
  // ' " ( ) ; and \ intact, and every one of those can escape that context: an
  // apostrophe closes the CSS string, a semicolon ends the declaration and lets a
  // crafted value append its own. Percent-encode them *before* escapeHtml, so the
  // `;` that escapeHtml introduces as part of `&amp;` is not mangled in turn.
  const CSS_URL_UNSAFE = /['"();\\]/g;

  function safeCssUrl(value) {
    const url = String(value || '').trim();
    if (!url || !/^(https?:)?\/\//i.test(url)) return '';
    try {
      const encoded = encodeURI(url).replace(
        CSS_URL_UNSAFE,
        (char) => `%${char.charCodeAt(0).toString(16).toUpperCase().padStart(2, '0')}`
      );
      return escapeHtml(encoded);
    } catch (error) {
      return '';
    }
  }

  function safeColor(value) {
    const color = String(value || '').trim();
    if (/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(color)) return color;
    if (/^hsla?\([\d\s.,%+-]+\)$/i.test(color)) return color;
    if (/^rgba?\([\d\s.,%+-]+\)$/i.test(color)) return color;
    return '';
  }

  function firstString(source, fields) {
    for (const field of fields) {
      const value = source && source[field];
      if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return '';
  }

  function normalizeNotes(value) {
    if (Array.isArray(value)) return value.map((note) => String(note || '').trim()).filter(Boolean);
    if (typeof value === 'string') return value.split(/[,|]/).map((note) => note.trim()).filter(Boolean);
    return [];
  }

  function fallbackColors(name) {
    const palettes = {
      jasmine: ['#ead9e9', '#8c6388'],
      honeysuckle: ['#f4e6bd', '#9b762f'],
      frangipani: ['#f1d9ce', '#a96751'],
      'ylang ylang': ['#eee0a9', '#92772d'],
      gardenia: ['#dfe8d3', '#647b51'],
      tuberose: ['#e5dced', '#765b8e'],
      'divine lily': ['#dce7ee', '#587387']
    };
    return palettes[String(name || '').toLowerCase()] || ['#eadfe7', '#765b72'];
  }

  function parseJsonScript(scope, selector, label) {
    const builder = scope;
    const source = builder.querySelector(selector);
    const raw = source ? source.textContent.trim() : '';
    if (!raw || raw.includes('{{')) return null;
    try {
      let data = JSON.parse(raw);
      if (typeof data === 'string') data = JSON.parse(data);
      return data && typeof data === 'object' ? data : null;
    } catch (error) {
      console.error(`Pods Builder: ${label} is not valid JSON.`, error);
      return null;
    }
  }

  function normalizeFragrance(fragrance, index) {
    if (!fragrance || !String(fragrance.name || '').trim()) return null;
    const name = String(fragrance.name).trim();
    const sku = String(fragrance.sku || '').trim();
    const fallback = fallbackColors(name);
    return {
      ...fragrance,
      name,
      sku,
      id: sku || `${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${index}`,
      accent: safeColor(fragrance.bg_color || fragrance.bgColor) || fallback[0],
      accentStrong: safeColor(fragrance.accent_color || fragrance.accentColor) || fallback[1],
      mood: firstString(fragrance, ['mood']),
      description: firstString(fragrance, ['description', 'scent_description', 'scentDescription', 'detail']),
      notes: normalizeNotes(fragrance.notes || fragrance.fragrance_notes || fragrance.fragranceNotes || fragrance.scent_notes || fragrance.scentNotes),
      cardImage: safeUrl(firstString(fragrance, ['card_image_url', 'cardImageUrl', 'image_url', 'imageUrl'])),
      // Same image, encoded for the CSS url() context rather than an <img src>.
      cardImageCss: safeCssUrl(firstString(fragrance, ['card_image_url', 'cardImageUrl', 'image_url', 'imageUrl'])),
      detailImage: safeUrl(firstString(fragrance, ['detail_image_url', 'detailImageUrl', 'lifestyle_image_url', 'lifestyleImageUrl', 'card_image_url', 'cardImageUrl', 'image_url', 'imageUrl'])),
      // The wide card artwork: tint and flower are baked in, so the card only has
      // to place the copy over it. Falls back to nothing, and the card then keeps
      // its flat tinted background.
      bannerImage: safeCssUrl(firstString(fragrance, ['banner_image_url', 'bannerImageUrl'])),
      // The artwork's own ground colour, sampled from its left margin. Distinct from
      // bg_color, which is the saturated swatch tone used for the dots — laying that
      // behind the banner turned Honeysuckle and Ylang Ylang visibly yellow.
      cardTint: safeColor(fragrance.card_tint || fragrance.cardTint)
    };
  }

  function ensureProperty(form, name) {
    let input = form.querySelector(`input[name="${name}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    return input;
  }

  // Shopify's own money format, handed over by the section. Set once per page, before
  // anything renders. The fallback only matters if the section failed to supply one.
  let moneyFormat = 'AED {{amount}}';

  function groupThousands(value, separator) {
    return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, separator);
  }

  // Supports the four amount tokens Shopify's money_format can carry. Anything else is
  // left untouched rather than guessed at, so an unfamiliar format degrades visibly
  // instead of silently printing a wrong number.
  function formatMoney(amount) {
    const value = Number(amount || 0);
    const cents = Math.round(value * 100);
    const decimals = (cents / 100).toFixed(2);
    const whole = String(Math.round(cents / 100));

    return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, (token, name) => {
      switch (name) {
        case 'amount':
          return groupThousands(decimals.split('.')[0], ',') + '.' + decimals.split('.')[1];
        case 'amount_no_decimals':
          return groupThousands(whole, ',');
        case 'amount_with_comma_separator':
          return groupThousands(decimals.split('.')[0], '.') + ',' + decimals.split('.')[1];
        case 'amount_no_decimals_with_comma_separator':
          return groupThousands(whole, '.');
        default:
          return token;
      }
    });
  }

  function initSelector(builder) {
    if (!builder || builder.dataset.podsSelectorReady === 'true') return;
    builder.dataset.podsSelectorReady = 'true';

    const requiredCount = Number.parseInt(builder.dataset.requiredCount || '4', 10);
    // Scope to the builder's own section. Falling back to <main> would sweep in the
    // quick-add forms of the related-products section and hijack their submits.
    const productRoot = builder.closest('.wt-product__main') || builder.closest('.shopify-section') || builder;
    const fragranceData = parseJsonScript(builder, '[data-pods-fragrance-data]', 'Fragrance Options metafield');
    const productData = parseJsonScript(builder, '[data-pods-product-data]', 'product variant data') || {};
    const fragrances = fragranceData && Array.isArray(fragranceData.fragrances)
      ? fragranceData.fragrances.map(normalizeFragrance).filter(Boolean)
      : [];
    const basePrice = Number(productData.price || 0);
    const baseVariantId = Number(productData.variantId);
    const productAvailable = productData.available !== false;
    // Read from an attribute rather than the JSON above: money_format contains "{{amount}}",
    // and parseJsonScript treats "{{" as un-rendered Liquid and discards the whole payload.
    if (builder.dataset.podsMoneyFormat && builder.dataset.podsMoneyFormat.trim()) {
      moneyFormat = builder.dataset.podsMoneyFormat;
    }

    // Curated sets: each is a list of fragrance names that fills the tray in one tap.
    const presetData = parseJsonScript(document, '[data-pods-presets]', 'curated sets') || {};
    const presets = Array.isArray(presetData.presets) ? presetData.presets : [];

    // Box tiers map onto real Shopify variants. A tier with no variant id is dormant,
    // so the ladder simply does not render until the variants exist.
    const tierData = parseJsonScript(document, '[data-pods-tiers]', 'box tiers') || {};
    const tiers = (Array.isArray(tierData.tiers) ? tierData.tiers : [])
      .filter((tier) => Number(tier.variantId) > 0);

    const optionsElement = builder.querySelector('[data-pods-options]');
    // Queried live rather than cached: the tray is mirrored in the mobile sticky bar and
    // the final reminder, and those live outside the builder element.
    const allSlots = () => Array.from(document.querySelectorAll('[data-pods-slot]'));
    const countElements = () => Array.from(document.querySelectorAll('[data-pods-count]'));
    const statusElements = () => Array.from(document.querySelectorAll('[data-pods-status]'));
    const summaryElements = () => Array.from(document.querySelectorAll('[data-pods-summary]'));
    const detailsElement = builder.querySelector('[data-pods-details]');
    const detailsContent = builder.querySelector('[data-pods-details-content]');
    const toastElement = builder.querySelector('[data-pods-toast]');
    const state = {
      bundle: Array(requiredCount).fill(null),
      quantities: {},
      totalItems: 0,
      cartHasPods: false,
      adding: false,
      variantError: '',
      tierId: tiers.length ? tiers[0].id : null,
      presetId: null
    };
    let activeDetailsId = null;
    let lastFocusedElement = null;
    let toastTimeout = null;
    let giftMounted = false;

    fragrances.forEach((fragrance) => { state.quantities[fragrance.id] = 0; });

    // Must stay in step with the Liquid fallback in pod-buy-column.liquid, which is
    // what shows pre-hydration; the JS relabels the button on load. Deliberately NOT
    // the section heading ("Build Your PODS") — the card and the primary CTA are kept
    // distinct so the page never reads as two competing calls to action.
    const INCOMPLETE_LABEL = 'Choose 4 fragrances';
    const ADDING_LABEL = 'Adding…';
    const CHECKOUT_LABEL = 'Checkout';

    function getFragrance(id) {
      return fragrances.find((fragrance) => fragrance.id === id);
    }

    function selectedFragrances() {
      return state.bundle.filter(Boolean);
    }

    function selectionSummary() {
      const counts = new Map();
      selectedFragrances().forEach((fragrance) => counts.set(fragrance.name, (counts.get(fragrance.name) || 0) + 1));
      return Array.from(counts, ([name, count]) => (count > 1 ? `${name} × ${count}` : name)).join(', ');
    }

    function activeTier() {
      if (!tiers.length) return null;
      return tiers.find((tier) => String(tier.id) === String(state.tierId)) || tiers[0];
    }

    // Shopify is the only source of truth for what a thing costs. A tier may name its
    // own price, but only because multi-box tiers map to variants this page has no data
    // for; the single-box tier is the current variant, so it must defer to Shopify or
    // the page would promise a price the cart does not charge.
    function activePrice() {
      const tier = activeTier();
      const tierPrice = tier ? Number(tier.price) : NaN;
      return Number.isFinite(tierPrice) && tierPrice > 0 ? tierPrice : basePrice;
    }

    function activeVariantId() {
      const tier = activeTier();
      return tier ? Number(tier.variantId) : baseVariantId;
    }

    function selectedVariant() {
      const id = activeVariantId();
      if (!Number.isFinite(id) || id <= 0) throw new Error('The Pods product variant is missing.');
      if (!productAvailable) throw new Error('The Pods product is currently unavailable.');
      return { id: id, price: activePrice(), available: true };
    }

    function productForms() {
      // An explicit target wins: the builder sheet and its form sit in different sections.
      const declared = builder.dataset.podsForm;
      if (declared) return Array.from(document.querySelectorAll(declared));
      const localForms = Array.from(productRoot.querySelectorAll(formSelector));
      return localForms.length ? localForms : Array.from(document.querySelectorAll(formSelector));
    }

    function mainProductForm() {
      return productForms().find((form) => form.querySelector('input[name="id"]')) || productForms()[0] || null;
    }

    function setButtonLabel(button, label) {
      if (!button) return;
      const labelElement = button.querySelector('span:not(.loading-overlay__spinner)') || button;
      labelElement.textContent = label;
      button.setAttribute('aria-label', label);
    }

    function getCtaLabel() {
      if (state.adding) return ADDING_LABEL;
      if (state.cartHasPods && state.totalItems === 0) return CHECKOUT_LABEL;
      if (state.totalItems === requiredCount) return `Add your set — ${formatMoney(activePrice())}`;
      if (state.totalItems === 0) return INCOMPLETE_LABEL;
      return `Choose ${requiredCount - state.totalItems} more`;
    }

    function showToast(message, duration) {
      if (!toastElement) return;
      if (toastTimeout) window.clearTimeout(toastTimeout);
      toastElement.textContent = message;
      toastElement.classList.add('is-visible');
      toastTimeout = window.setTimeout(() => toastElement.classList.remove('is-visible'), duration || 2200);
    }

    function actionMarkup(fragrance, detail) {
      const count = state.quantities[fragrance.id] || 0;
      const full = state.totalItems >= requiredCount;
      const detailClass = detail ? ' pods-fragrance-selector__details-action' : '';
      if (!count) {
        return `<button class="${detailClass.trim()}" type="button" data-pods-action="add" data-pods-id="${escapeHtml(fragrance.id)}" aria-label="Add ${escapeHtml(fragrance.name)} to your pod set" ${full ? 'disabled' : ''}>Add</button>`;
      }
      return `
        <div class="pods-fragrance-selector__quantity${detailClass}" aria-label="${escapeHtml(fragrance.name)} quantity">
          <button type="button" data-pods-action="decrease" data-pods-id="${escapeHtml(fragrance.id)}" aria-label="Remove one ${escapeHtml(fragrance.name)}">−</button>
          <span aria-live="polite">${count}</span>
          <button type="button" data-pods-action="increase" data-pods-id="${escapeHtml(fragrance.id)}" aria-label="Add another ${escapeHtml(fragrance.name)}" ${full ? 'disabled' : ''}>+</button>
        </div>`;
    }

    // The grid card uses its own small round control. The details dialog keeps the
    // wider button from actionMarkup(), where there is room and only one to press.
    function cardControlMarkup(fragrance) {
      const count = state.quantities[fragrance.id] || 0;
      const full = state.totalItems >= requiredCount;
      const id = escapeHtml(fragrance.id);
      const name = escapeHtml(fragrance.name);
      if (!count) {
        return `<button class="pods-frag__add" type="button" data-pods-action="add" data-pods-id="${id}" aria-label="Add ${name} to your set"${full ? ' disabled' : ''}><span aria-hidden="true">+</span></button>`;
      }
      return `
        <div class="pods-frag__qty" role="group" aria-label="${name} quantity">
          <button type="button" data-pods-action="decrease" data-pods-id="${id}" aria-label="Remove one ${name}"><span aria-hidden="true">&#8722;</span></button>
          <span class="pods-frag__count" aria-live="polite">${count}</span>
          <button type="button" data-pods-action="increase" data-pods-id="${id}" aria-label="Add another ${name}"${full ? ' disabled' : ''}><span aria-hidden="true">+</span></button>
        </div>`;
    }

    function visualMarkup(fragrance, detail) {
      const image = detail ? fragrance.detailImage : fragrance.cardImage;
      if (image) return `<img src="${image}" alt="${escapeHtml(fragrance.name)}" loading="${detail ? 'eager' : 'lazy'}">`;
      return `<span class="pods-fragrance-selector__visual-initial" aria-hidden="true">${escapeHtml(fragrance.name.charAt(0))}</span>`;
    }

    function renderCards() {
      if (!optionsElement) return;
      if (!fragrances.length) {
        optionsElement.innerHTML = '<div class="pods-fragrance-selector__error" role="alert"><p>Fragrance options could not be loaded. Check this product’s Fragrance Options metafield.</p></div>';
        return;
      }
      optionsElement.innerHTML = fragrances.map((fragrance) => {
        const count = state.quantities[fragrance.id] || 0;
        // One info target instead of two (the thumbnail and a "Details" button both
        // opened the same dialog), so the row carries two controls, not three.
        return `
          <article class="pods-frag${count ? ' is-selected' : ''}${fragrance.bannerImage ? ' has-banner' : ''}" data-pods-fragrance="${escapeHtml(fragrance.name)}" data-pods-id="${escapeHtml(fragrance.id)}" ${count ? `data-selected-count="${count}"` : ''} style="--pod-accent: ${fragrance.accent}; --pod-accent-strong: ${fragrance.accentStrong};${fragrance.bannerImage ? ` --pod-banner: url('${fragrance.bannerImage}');` : ''}${fragrance.cardTint ? ` --pod-tint: ${fragrance.cardTint};` : ''}${fragrance.cardImageCss ? ` --pod-square: url('${fragrance.cardImageCss}');` : ''}">
            <button class="pods-frag__info" type="button" data-pods-action="details" data-pods-id="${escapeHtml(fragrance.id)}" aria-label="View ${escapeHtml(fragrance.name)} fragrance details">
              <span class="pods-frag__thumb${fragrance.cardImage ? ' has-image' : ''}">${visualMarkup(fragrance, false)}</span>
              <span class="pods-frag__text">
                <span class="pods-frag__name">${escapeHtml(fragrance.name)}</span>
                ${fragrance.mood ? `<span class="pods-frag__mood">${escapeHtml(fragrance.mood)}</span>` : ''}
              </span>
            </button>
            <div class="pods-frag__control">${cardControlMarkup(fragrance)}</div>
          </article>`;
      }).join('');
    }

    function renderDetails() {
      const fragrance = getFragrance(activeDetailsId);
      if (!detailsContent || !fragrance) return;
      const count = state.quantities[fragrance.id] || 0;
      const selectionStatus = count ? `${count} selected in your pod set` : state.totalItems >= requiredCount ? 'Your pod set is full' : 'Ready to add';
      detailsContent.innerHTML = `
        <div class="pods-fragrance-selector__details-visual${fragrance.detailImage ? ' has-image' : ''}" style="--pod-accent: ${fragrance.accent}; --pod-accent-strong: ${fragrance.accentStrong};">${visualMarkup(fragrance, true)}</div>
        <div class="pods-fragrance-selector__details-info">
          <p class="pods-story__eyebrow">Fragrance profile</p>
          <h3 id="pods-fragrance-details-title">${escapeHtml(fragrance.name)}</h3>
          ${fragrance.description ? `<p class="pods-fragrance-selector__details-description">${escapeHtml(fragrance.description)}</p>` : ''}
          ${fragrance.mood ? `<div class="pods-fragrance-selector__details-section"><strong>Mood</strong><p>${escapeHtml(fragrance.mood)}</p></div>` : ''}
          ${fragrance.notes.length ? `<div class="pods-fragrance-selector__details-section"><strong>Scent notes</strong><ul>${fragrance.notes.map((note) => `<li>${escapeHtml(note)}</li>`).join('')}</ul></div>` : ''}
          <div class="pods-fragrance-selector__details-footer">
            <p>${escapeHtml(selectionStatus)}</p>
            <div class="pods-fragrance-selector__action">${actionMarkup(fragrance, true)}</div>
          </div>
        </div>`;
    }

    function openDetails(id) {
      if (!detailsElement || !getFragrance(id)) return;
      activeDetailsId = id;
      lastFocusedElement = document.activeElement;
      renderDetails();
      detailsElement.classList.add('is-open');
      detailsElement.setAttribute('aria-hidden', 'false');
      document.body.classList.add('pods-details-open');
      window.requestAnimationFrame(() => detailsElement.querySelector('.pods-fragrance-selector__details-card')?.focus());
    }

    function closeDetails() {
      if (!detailsElement) return;
      detailsElement.classList.remove('is-open');
      detailsElement.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('pods-details-open');
      activeDetailsId = null;
      if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') lastFocusedElement.focus();
      lastFocusedElement = null;
    }

    function updateSlots() {
      allSlots().forEach((slot) => {
        const index = (Number.parseInt(slot.dataset.podsSlot, 10) || 1) - 1;
        const fragrance = state.bundle[index];
        if (!fragrance) {
          slot.className = 'pods-fragrance-selector__slot is-empty';
          slot.removeAttribute('style');
          slot.setAttribute('aria-label', `Pod ${index + 1} is empty`);
          slot.innerHTML = `<span class="pods-fragrance-selector__slot-plus" aria-hidden="true">+</span><span class="pods-fragrance-selector__slot-label">Pod ${index + 1}</span>`;
          return;
        }
        slot.className = 'pods-fragrance-selector__slot is-filled';
        slot.setAttribute('aria-label', `Pod ${index + 1}: ${fragrance.name}`);
        slot.style.setProperty('--slot-accent', fragrance.accent);
        slot.style.setProperty('--slot-strong', fragrance.accentStrong);
        const slotImage = fragrance.cardImage || fragrance.detailImage;
        const slotVisual = slotImage
          ? `<span class="pods-fragrance-selector__slot-media" aria-hidden="true"><img src="${slotImage}" alt="" loading="lazy"></span>`
          : `<span class="pods-fragrance-selector__slot-flower" aria-hidden="true">${escapeHtml(fragrance.name.charAt(0))}</span>`;
        slot.innerHTML = `
          <button class="pods-fragrance-selector__slot-remove" type="button" data-pods-action="remove-slot" data-slot-index="${index}" data-pods-id="${escapeHtml(fragrance.id)}" aria-label="Remove ${escapeHtml(fragrance.name)} from pod ${index + 1}">×</button>
          ${slotVisual}
          <span class="pods-fragrance-selector__slot-name">${escapeHtml(fragrance.name)}</span>`;
      });
    }

    function updateStatus() {
      const remaining = requiredCount - state.totalItems;
      countElements().forEach((element) => { element.textContent = String(state.totalItems); });
      let message;
      if (!state.totalItems) message = 'No pods chosen yet.';
      else if (remaining > 0) message = `Good choice. Choose ${remaining} more ${remaining === 1 ? 'pod' : 'pods'} to complete your set.`;
      else if (state.variantError) message = state.variantError;
      else message = `Excellent, your pod set is complete: ${selectionSummary()}.`;

      statusElements().forEach((element) => {
        element.classList.toggle('is-complete', remaining === 0);
        element.classList.toggle('is-incomplete', state.totalItems > 0 && remaining > 0);
        // The empty state still differs by place: inside the sheet you are already
        // choosing, so pointing at the builder is not advice you can act on there.
        if (!state.totalItems) {
          element.textContent = element.closest('[data-pods-sheet]')
            ? 'Choose four fragrances to fill your set.'
            : 'No pods chosen yet — build your set of four.';
          return;
        }
        element.textContent = message;
      });

      summaryElements().forEach((element) => {
        element.textContent = state.totalItems ? selectionSummary() : 'No pods chosen yet';
      });

      document.body.classList.toggle('pods-builder-started', state.totalItems > 0);

      // Set status under the sticky tray: how far along, or that it is done.
      const shortfall = requiredCount - state.totalItems;
      document.querySelectorAll('[data-pods-sticky-status]').forEach((element) => {
        element.textContent = state.totalItems === 0
          ? `Choose ${requiredCount} pods`
          : shortfall > 0
            ? `${state.totalItems} of ${requiredCount} chosen · ${shortfall} to go`
            : `Set complete · ${requiredCount} pods`;
        element.classList.toggle('is-complete', shortfall === 0 && state.totalItems > 0);
      });
    }

    function updatePricing() {
      const price = formatMoney(activePrice());
      document.querySelectorAll('[data-pods-total]').forEach((element) => { element.textContent = price; });
      document.querySelectorAll('[data-pods-tier]').forEach((element) => {
        element.classList.toggle('is-selected', String(element.dataset.podsTier) === String(state.tierId));
        element.setAttribute('aria-pressed', String(element.dataset.podsTier) === String(state.tierId) ? 'true' : 'false');
      });
    }

    function updateVariantState() {
      state.variantError = '';
      if (state.totalItems !== requiredCount) return null;
      try {
        return selectedVariant();
      } catch (error) {
        state.variantError = error.message;
        console.error('Pods Builder: Product variant is unavailable.', error.message);
        return null;
      }
    }

    function updateFormInputs(variant) {
      productForms().forEach((form) => {
        state.bundle.forEach((fragrance, index) => {
          ensureProperty(form, `properties[Pod ${index + 1}]`).value = fragrance ? fragrance.name : '';
        });
        if (variant) {
          const variantInput = form.querySelector('input[name="id"]');
          if (variantInput) {
            variantInput.value = String(variant.id);
            variantInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });
    }

    function updateButtonStates() {
      const complete = state.totalItems === requiredCount;
      const checkout = state.cartHasPods && state.totalItems === 0;
      const label = getCtaLabel();
      productForms().forEach((form) => {
        const submitButton = form.querySelector('[type="submit"]');
        if (submitButton) {
          if (submitButton.dataset.podsOriginalDisabled === undefined) submitButton.dataset.podsOriginalDisabled = submitButton.disabled ? 'true' : 'false';
          const unavailable = submitButton.dataset.podsOriginalDisabled === 'true' || !productAvailable;
          // Enabled whenever the product is sellable. An incomplete set is not a
          // reason to show a dead button: the label says what the next step is and
          // the click performs it (submit opens the builder until four are chosen).
          // A greyed-out primary in the add-to-cart slot reads as "out of stock".
          if (!unavailable && !state.adding) {
            submitButton.disabled = false;
            submitButton.removeAttribute('aria-disabled');
          } else {
            submitButton.disabled = true;
            submitButton.setAttribute('aria-disabled', 'true');
          }
          submitButton.classList.toggle('loading', state.adding);
          submitButton.setAttribute('aria-busy', state.adding ? 'true' : 'false');
          if (!unavailable) setButtonLabel(submitButton, label);
        }
        const paymentContainer = form.querySelector('.shopify-payment-button');
        if (paymentContainer) {
          paymentContainer.hidden = !complete || !productAvailable;
          paymentContainer.style.visibility = complete && productAvailable ? '' : 'hidden';
          paymentContainer.querySelectorAll('button, [role="button"]').forEach((button) => {
            button.disabled = !complete || !productAvailable;
            button.setAttribute('aria-disabled', complete && productAvailable ? 'false' : 'true');
          });
        }
      });

      document.body.classList.toggle('pods-builder-complete', complete);
      document.body.classList.toggle('pods-builder-incomplete', !complete);
      document.body.classList.toggle('pods-builder-adding', state.adding);
      document.querySelectorAll(ctaSelector).forEach((button) => {
        setButtonLabel(button, label);
        button.classList.toggle('pods-sticky-ready', complete || checkout);
        button.classList.toggle('pods-sticky-incomplete', !complete && !checkout);
        button.classList.toggle('pods-sticky-checkout', checkout);
        button.classList.toggle('loading', state.adding);
        if (state.adding || !productAvailable) {
          button.disabled = true;
          button.setAttribute('aria-disabled', 'true');
        } else {
          button.removeAttribute('disabled');
          // Same reasoning as the in-column button: it always does something.
          button.removeAttribute('aria-disabled');
        }
      });
    }

    function findByName(name) {
      const wanted = String(name || '').trim().toLowerCase();
      return fragrances.find((fragrance) => fragrance.name.trim().toLowerCase() === wanted) || null;
    }

    function clearBundle() {
      state.bundle = Array(requiredCount).fill(null);
      state.totalItems = 0;
      Object.keys(state.quantities).forEach((id) => { state.quantities[id] = 0; });
    }

    function applyPreset(presetId) {
      const preset = presets.find((item) => String(item.id) === String(presetId));
      if (!preset) return;

      // A chosen set toggles off, so the control behaves the way it looks.
      if (String(state.presetId) === String(preset.id)) {
        clearBundle();
        state.presetId = null;
        update();
        showToast(`${preset.name} removed`);
        return;
      }
      const chosen = (preset.fragrances || []).map(findByName).filter(Boolean).slice(0, requiredCount);
      if (chosen.length !== requiredCount) {
        showToast(`${preset.name} is unavailable right now. Choose your four instead.`, 4000);
        console.error('Pods Builder: preset does not resolve to four available fragrances.', preset);
        return;
      }
      clearBundle();
      chosen.forEach((fragrance, index) => {
        state.bundle[index] = fragrance;
        state.quantities[fragrance.id] = (state.quantities[fragrance.id] || 0) + 1;
      });
      state.totalItems = requiredCount;
      state.presetId = preset.id;
      update();
      showToast(`${preset.name} selected`);
    }

    function selectTier(tierId) {
      if (!tiers.some((tier) => String(tier.id) === String(tierId))) return;
      state.tierId = tierId;
      update();
    }

    function renderTiers() {
      const group = document.querySelector('[data-pods-tier-group]');
      const list = document.querySelector('[data-pods-tier-list]');
      if (!group || !list) return;
      // A ladder needs at least two rungs; until a second variant exists it stays hidden.
      if (tiers.length < 2) {
        group.hidden = true;
        return;
      }
      group.hidden = false;
      if (list.dataset.podsRendered === 'true') return;
      list.dataset.podsRendered = 'true';
      list.innerHTML = tiers.map((tier) => `
        <button class="pods-tier" type="button" data-pods-action="tier" data-pods-tier="${escapeHtml(tier.id)}" aria-pressed="false">
          ${tier.badge ? `<span class="pods-tier__badge">${escapeHtml(tier.badge)}</span>` : ''}
          <span class="pods-tier__count">${escapeHtml(tier.label)}</span>
          <span class="pods-tier__price">${formatMoney(tier.price)}</span>
          ${Number(tier.saving) > 0 ? `<span class="pods-tier__saving">Save ${formatMoney(tier.saving)}</span>` : ''}
        </button>`).join('');
    }

    function renderChooser() {
      document.querySelectorAll('[data-pods-preset]').forEach((element) => {
        const on = String(element.dataset.podsPreset) === String(state.presetId);
        element.classList.toggle('is-selected', on);
        element.setAttribute('aria-pressed', on ? 'true' : 'false');
      });

      // The custom row doubles as the summary of a hand-built set.
      document.querySelectorAll('[data-pods-custom]').forEach((element) => {
        const custom = !state.presetId && state.totalItems > 0;
        element.classList.toggle('is-selected', custom);
        element.setAttribute('aria-pressed', custom ? 'true' : 'false');
        // The heading stays put; the supporting line reports the state. Swapping the
        // heading for a four-name list set a fragrance list in 22px display type.
        const hint = element.querySelector('[data-pods-custom-hint]');
        if (hint) hint.textContent = custom ? selectionSummary() : 'Seven to choose from. Repeats allowed.';
        // The card only appears once a set exists, and the primary CTA below states
        // what is still needed — so this is just the way back in, not a second CTA.
        const action = element.querySelector('[data-pods-custom-action]');
        if (action) action.textContent = 'Edit';

        // The chosen four as swatches. Reading four names takes a beat; four
        // colours reads as "this is my box" at a glance. Cleared when empty so
        // the row never shows a stale set after the cart is emptied.
        const dots = element.querySelector('[data-pods-custom-dots]');
        if (dots) {
          if (!custom) {
            dots.innerHTML = '';
          } else {
            dots.innerHTML = selectedFragrances().map((fragrance) => {
              const tone = safeColor(fragrance.accent) || 'transparent';
              return `<i style="--swatch: ${escapeHtml(tone)}" title="${escapeHtml(fragrance.name)}"></i>`;
            }).join('');
          }
        }
      });
    }

    function renderAddedNotice() {
      document.querySelectorAll('[data-pods-added]').forEach((element) => {
        // Hidden as soon as a new set is being built, so it never competes with the CTA.
        if (!state.cartHasPods || state.totalItems > 0 || !state.lastAdded) {
          element.hidden = true;
          return;
        }
        element.hidden = false;
        // Two blocks rather than one run of text: what was added is a confirmation and
        // the second line is an invitation to buy again. Run together they read as one
        // sentence, and a long set — four names plus their counts — pushed the prompt
        // to start mid-line, wrapping differently on every order.
        element.innerHTML = `<strong>${escapeHtml(state.lastAdded)} added to your cart.</strong>`
          + '<span class="pods-buy__added-more">Want a second box with different pods? Build another set above.</span>';
      });
    }

    function sheetElement() {
      return document.querySelector('[data-pods-sheet]');
    }

    function openSheet() {
      const sheet = sheetElement();
      if (!sheet) return;
      lastFocusedElement = document.activeElement;
      sheet.hidden = false;
      sheet.classList.add('is-open');
      sheet.setAttribute('aria-hidden', 'false');
      document.body.classList.add('pods-sheet-open');
      // The toast is centred at the bottom of the viewport, which is where the sheet's
      // footer lives. Publish the footer's measured height so the toast sits above the
      // Done button instead of on top of it.
      const foot = builder.querySelector('.pods-sheet__foot');
      if (foot) {
        document.documentElement.style.setProperty(
          '--pods-sheet-foot-h', `${Math.round(foot.getBoundingClientRect().height)}px`
        );
      }
      window.requestAnimationFrame(() => {
        sheet.querySelector('[data-pods-sheet-panel]')?.focus();
      });
    }

    function closeSheet() {
      const sheet = sheetElement();
      if (!sheet || !sheet.classList.contains('is-open')) return;
      sheet.classList.remove('is-open');
      sheet.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('pods-sheet-open');
      window.setTimeout(() => { sheet.hidden = true; }, 200);
      if (lastFocusedElement && document.contains(lastFocusedElement)) lastFocusedElement.focus();
    }

    function update() {
      const variant = updateVariantState();
      updateSlots();
      renderCards();
      updatePricing();
      updateFormInputs(variant);
      updateStatus();
      updateButtonStates();
      syncGiftVisibility();
      renderTiers();
      renderChooser();
      renderAddedNotice();
      if (activeDetailsId) renderDetails();
      builder.dispatchEvent(new CustomEvent('pods:selection-change', {
        bubbles: true,
        detail: {
          selected: selectedFragrances().map((fragrance) => fragrance.name),
          complete: state.totalItems === requiredCount,
          total: activePrice(),
          variantId: variant ? variant.id : null
        }
      }));
    }

    function add(id) {
      const fragrance = getFragrance(id);
      if (!fragrance || state.totalItems >= requiredCount) {
        showToast(`Maximum ${requiredCount} pods allowed`);
        return;
      }
      const emptySlot = state.bundle.findIndex((item) => item === null);
      if (emptySlot < 0) return;
      state.cartHasPods = false;
      state.bundle[emptySlot] = fragrance;
      state.quantities[id] = (state.quantities[id] || 0) + 1;
      state.totalItems += 1;
      update();
      showToast(`${fragrance.name} added to your pod set`);
    }

    function removeLast(id) {
      const slotIndex = state.bundle.findIndex((fragrance) => fragrance && fragrance.id === id);
      if (slotIndex < 0) return;
      const fragrance = state.bundle[slotIndex];
      state.bundle[slotIndex] = null;
      state.quantities[id] = Math.max(0, (state.quantities[id] || 0) - 1);
      state.totalItems -= 1;
      update();
      showToast(`${fragrance.name} removed from your pod set`);
    }

    function removeSlot(index) {
      const fragrance = state.bundle[index];
      if (!fragrance) return;
      state.bundle[index] = null;
      state.quantities[fragrance.id] = Math.max(0, (state.quantities[fragrance.id] || 0) - 1);
      state.totalItems -= 1;
      update();
      showToast(`${fragrance.name} removed from your pod set`);
    }

    function mountGiftWrap() {
      // Document scope, not builder scope. `builder` is the .pods-fragrance-selector
      // sheet, and the mount lives in the buy column outside it — so this never found
      // its target and the theme's gift widget simply stayed where the theme rendered
      // it, at the foot of the product info.
      const giftStep = document.querySelector('[data-pods-gift-step]');
      const giftMount = document.querySelector('[data-pods-gift-mount]');
      const giftWrap = document.querySelector('#gift-wrap-selector');
      if (!giftStep || !giftMount || !giftWrap) return false;
      if (giftWrap.parentElement !== giftMount) giftMount.appendChild(giftWrap);
      giftMounted = true;
      syncGiftVisibility();
      return true;
    }

    // Gifting is a decision about a finished set, so the option waits until there is
    // one. It also keeps the empty state down to heading, copy and a single button.
    function syncGiftVisibility() {
      const giftStep = document.querySelector('[data-pods-gift-step]');
      if (!giftStep) return;
      giftStep.hidden = !giftMounted || state.totalItems !== requiredCount;
    }

    function giftWrapLineItem(quantity, properties) {
      const giftWrap = document.querySelector('#gift-wrap-selector');
      if (!giftWrap || !giftWrap.querySelector('.gift-wrap__checkbox')?.checked) return null;
      const styleName = giftWrap.querySelector('.gift-wrap__input-style')?.value || '';
      const wrapType = giftWrap.querySelector('.gift-wrap__input-type')?.value || '';
      const message = giftWrap.querySelector('.gift-wrap__message-input')?.value?.trim() || '';
      if (styleName) properties['Gift Style'] = styleName;
      if (wrapType) properties._gift_wrap_type = wrapType;
      if (message) properties['Gift Message'] = message;
      const variantId = Number(wrapType === 'premium' ? giftWrap.dataset.premiumVariantId : giftWrap.dataset.standardVariantId);
      if (!Number.isFinite(variantId)) return null;
      const productTitle = giftWrap.dataset.productTitle || '';
      return { id: variantId, quantity, properties: { _linked_product: productTitle, For: productTitle, Style: styleName } };
    }

    function cartDrawerSectionIds() {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        const ids = drawer.getSectionsToRender().map((section) => section && section.id).filter(Boolean);
        if (ids.length) return ids;
      }
      return ['cart-drawer', 'cart-icon-bubble'];
    }

    function addSectionRendering(payload) {
      payload.sections = cartDrawerSectionIds().join(',');
      payload.sections_url = window.location.pathname;
      return payload;
    }

    function buildCartPayload(form) {
      const variant = selectedVariant();
      const quantity = Number.parseInt(form.querySelector('[name="quantity"]')?.value || '1', 10) || 1;
      const properties = {};
      state.bundle.forEach((fragrance, index) => {
        if (fragrance) properties[`Pod ${index + 1}`] = fragrance.name;
      });
      const mainItem = { id: variant.id, quantity, properties };
      const giftItem = giftWrapLineItem(quantity, properties);
      return {
        variant,
        mainItem,
        giftItem,
        payload: giftItem ? addSectionRendering({ items: [mainItem, giftItem] }) : addSectionRendering(mainItem)
      };
    }

    function clearSelectionAfterAdd() {
      const added = state.presetId
        ? (presets.find((item) => String(item.id) === String(state.presetId)) || {}).name
        : selectionSummary();
      state.bundle = Array(requiredCount).fill(null);
      state.totalItems = 0;
      Object.keys(state.quantities).forEach((id) => { state.quantities[id] = 0; });
      state.presetId = null;
      state.lastAdded = added || '';
      state.cartHasPods = true;
      state.adding = false;
      document.dispatchEvent(new CustomEvent('cart:gift-wrap-reset'));
      update();
    }

    function openCartDrawer(data) {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) {
        window.location.href = window.routes?.cart_url || '/cart';
        return;
      }

      const isOpen = drawer.hasAttribute('open')
        || drawer.isOpen === true
        || document.body.classList.contains('page-overlay-cart-on');

      // cart-drawer.js: renderContents(state, isClosedCart) queues its OWN
      // toggleDrawerClasses() in a setTimeout when isClosedCart is true. Toggling
      // again here ran first — the drawer opened, then that queued toggle closed
      // it. Let renderContents do the opening and do not touch it afterwards.
      if (data && data.sections && typeof drawer.renderContents === 'function') {
        drawer.renderContents({ sections: data.sections }, !isOpen);
        return;
      }

      // The fallbacks re-render without opening, so they still need the toggle.
      if (typeof drawer.refreshCartDrawer === 'function') {
        drawer.refreshCartDrawer({});
      } else {
        drawer.dispatchEvent(new CustomEvent('cart-drawer:refresh', { detail: { response: data }, bubbles: true, composed: true }));
      }

      if (isOpen) return;
      if (typeof drawer.toggleDrawerClasses === 'function') {
        drawer.toggleDrawerClasses();
      } else {
        drawer.setAttribute('open', '');
        document.body.classList.add('page-overlay-cart-on');
      }
    }

    async function postCartPayload(payload) {
      const response = await window.fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!response.ok || data.status) throw data;
      return data;
    }

    async function addToCart(form) {
      let cartBuild;
      try {
        cartBuild = buildCartPayload(form);
      } catch (error) {
        showToast(error.message || 'The Pods product is currently unavailable.', 5000);
        console.error('Pods Builder: Product variant unavailable.', error.message);
        return;
      }

      state.adding = true;
      updateButtonStates();
      try {
        if (document.documentElement.dataset.podsPreviewCart === 'true') {
          window.__podsPreviewLastCartPayload = cartBuild.payload;
          builder.dataset.podsPreviewLastCartPayload = JSON.stringify(cartBuild.payload);
          await new Promise((resolve) => window.setTimeout(resolve, 180));
          clearSelectionAfterAdd();
          showToast('Pod set added to the preview cart');
          return;
        }

        let data;
        try {
          data = await postCartPayload(cartBuild.payload);
        } catch (error) {
          if (!cartBuild.giftItem) throw error;
          data = await postCartPayload(addSectionRendering(cartBuild.mainItem));
          try {
            const giftData = await postCartPayload(addSectionRendering(cartBuild.giftItem));
            if (giftData && giftData.sections) data = giftData;
          } catch (giftError) {
            console.warn('Pods Builder: Gift wrap could not be added.', giftError);
          }
        }

        if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof publish === 'function') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: cartBuild.variant.id, cartData: data });
        }
        openCartDrawer(data);
        clearSelectionAfterAdd();
      } catch (error) {
        state.adding = false;
        updateButtonStates();
        const message = error && (error.description || error.message) ? error.description || error.message : 'Unable to add this pod set to cart.';
        showToast(message, 4000);
        console.error('Pods Builder: Cart API error.', error);
      }
    }

    function setupForms() {
      productForms().forEach((form) => {
        if (form.dataset.podsCartReady === 'true') return;
        form.dataset.podsCartReady = 'true';
        form.addEventListener('submit', (event) => {
          if (state.cartHasPods && state.totalItems === 0) {
            event.preventDefault();
            event.stopImmediatePropagation();
            if (document.documentElement.dataset.podsPreviewCart === 'true') showToast('Checkout would open on Shopify');
            else window.location.href = '/checkout';
            return;
          }
          if (state.totalItems !== requiredCount) {
            // One button carries the whole journey, so pressing it mid-way is not an
            // error to shake at — it is the customer asking for the next step.
            event.preventDefault();
            event.stopImmediatePropagation();
            openSheet();
            return;
          }
          event.preventDefault();
          event.stopImmediatePropagation();
          addToCart(form);
        }, true);
      });
    }

    function setupChooser() {
      if (document.body.dataset.podsChooserReady === 'true') return;
      document.body.dataset.podsChooserReady = 'true';

      document.addEventListener('click', (event) => {
        const control = event.target.closest('[data-pods-action]');
        if (!control || control.disabled) return;
        const action = control.dataset.podsAction;
        if (action === 'preset') {
          event.preventDefault();
          applyPreset(control.dataset.podsPreset);
        } else if (action === 'tier') {
          event.preventDefault();
          selectTier(control.dataset.podsTier);
        } else if (action === 'open-sheet') {
          event.preventDefault();
          openSheet();
        } else if (action === 'close-sheet') {
          event.preventDefault();
          closeSheet();
        }
      });

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        if (activeDetailsId) return; // the fragrance dialog handles its own Escape
        closeSheet();
      });
    }

    function setupStickyBridge() {
      if (document.body.dataset.podsStickyBridge === 'true') return;
      document.body.dataset.podsStickyBridge = 'true';
      document.addEventListener('click', (event) => {
        const button = event.target.closest(ctaSelector);
        if (!button || !document.querySelector(selectorName)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (state.cartHasPods && state.totalItems === 0) {
          if (document.documentElement.dataset.podsPreviewCart === 'true') showToast('Checkout would open on Shopify');
          else window.location.href = '/checkout';
          return;
        }
        if (state.totalItems !== requiredCount) {
          // The button states what is missing, so it should also be the way to fix it.
          // No toast and no scroll: the sheet that just opened carries its own status,
          // and the sheet is position:fixed, so scrolling it into view did nothing.
          openSheet();
          return;
        }
        const form = mainProductForm();
        if (!form) {
          showToast('Add to cart is not ready yet. Please try again.', 3000);
          return;
        }
        if (typeof form.requestSubmit === 'function') form.requestSubmit(form.querySelector('[type="submit"]'));
        else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }, true);
    }

    function setupPaymentButtons() {
      document.addEventListener('click', (event) => {
        const button = event.target.closest('.shopify-payment-button button, .shopify-payment-button [role="button"]');
        if (!button || !productRoot.contains(button)) return;
        if (state.totalItems !== requiredCount) {
          event.preventDefault();
          event.stopImmediatePropagation();
          showToast(`Please select ${requiredCount} pods before checkout`, 3000);
          builder.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        updateFormInputs(updateVariantState());
      }, true);
    }

    function setupCartDrawerCloseWatch() {
      if (document.body.dataset.podsCartCloseWatch === 'true') return;
      document.body.dataset.podsCartCloseWatch = 'true';
      const drawerOpen = () => {
        const drawer = document.querySelector('cart-drawer');
        return Boolean((drawer && (drawer.hasAttribute('open') || drawer.isOpen === true)) || document.body.classList.contains('page-overlay-cart-on'));
      };
      const resetCheckout = () => {
        if (!state.cartHasPods || drawerOpen() || document.documentElement.dataset.podsPreviewCart === 'true') return;
        state.cartHasPods = false;
        updateButtonStates();
      };
      new MutationObserver(resetCheckout).observe(document.body, { attributes: true, attributeFilter: ['class'] });
      const drawer = document.querySelector('cart-drawer');
      if (drawer) new MutationObserver(resetCheckout).observe(drawer, { attributes: true, attributeFilter: ['open'] });
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) resetCheckout();
      });
    }

    builder.addEventListener('click', (event) => {
      const closeControl = event.target.closest('[data-pods-details-close]');
      if (closeControl) {
        closeDetails();
        return;
      }
      const control = event.target.closest('[data-pods-action]');
      if (!control || control.disabled) return;
      const action = control.dataset.podsAction;
      if (action === 'confirm-sheet') {
        closeSheet();
        return;
      }
      if (action === 'clear-all') {
        if (!state.totalItems) return;
        clearBundle();
        state.presetId = null;
        update();
        showToast('Pod set cleared');
        return;
      }
      // 'details' is deliberately not handled on click — a single click on a card
      // should only ever add or remove. The dialog opens on dblclick (below), and
      // on Enter/Space so it stays reachable without a pointer.
      if (action === 'details') return;
      if (action === 'remove-slot') {
        state.presetId = null;
        removeSlot(Number.parseInt(control.dataset.slotIndex, 10));
        return;
      }
      const id = control.dataset.podsId;
      if (action === 'add' || action === 'increase') { state.presetId = null; add(id); }
      if (action === 'decrease') { state.presetId = null; removeLast(id); }
    });

    builder.addEventListener('dblclick', (event) => {
      const control = event.target.closest('[data-pods-action="details"]');
      if (!control || control.disabled) return;
      event.preventDefault();
      openDetails(control.dataset.podsId);
    });

    builder.addEventListener('keydown', (event) => {
      // Keyboard has no double click, so Enter/Space on a focused card opens the
      // profile directly.
      const info = event.target.closest('[data-pods-action="details"]');
      if (info && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        openDetails(info.dataset.podsId);
        return;
      }
      if (event.key === 'Escape' && activeDetailsId) closeDetails();
      if ((event.key === 'Enter' || event.key === ' ') && event.target.closest('[data-pods-slot].is-empty')) {
        event.preventDefault();
        builder.querySelector('[data-pods-action="add"]:not(:disabled)')?.focus();
      }
    });

    if (!fragrances.length) console.error('Pods Builder: No valid fragrances found in product.metafields.custom.fragrance_options.');
    mountGiftWrap();
    let giftRetries = 0;
    const giftTimer = window.setInterval(() => {
      giftRetries += 1;
      if (mountGiftWrap() || giftRetries >= 10) window.clearInterval(giftTimer);
    }, 250);
    setupForms();
    setupChooser();
    setupStickyBridge();
    setupPaymentButtons();
    setupCartDrawerCloseWatch();
    update();

    const formObserver = new MutationObserver((mutations) => {
      const purchaseControlsAdded = mutations.some((mutation) => Array.from(mutation.addedNodes).some((node) => {
        if (!(node instanceof Element)) return false;
        return node.matches(`${formSelector}, .shopify-payment-button`) || Boolean(node.querySelector(`${formSelector}, .shopify-payment-button`));
      }));
      if (!purchaseControlsAdded) return;
      setupForms();
      updateButtonStates();
    });
    formObserver.observe(productRoot, { childList: true, subtree: true });
  }

  function initAll(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(selectorName).forEach(initSelector);
    if (scope.matches && scope.matches(selectorName)) initSelector(scope);
  }

  window.initPodsFragranceSelectors = initAll;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  else initAll(document);
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
